import { useTranslation } from 'react-i18next';
import { 
    TextField, Checkbox, FormControlLabel, Switch, Select, MenuItem, 
    FormControl, InputLabel, FormHelperText, RadioGroup, Radio, 
    FormLabel, FormGroup, Typography 
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { extractBestTranslation } from '@utils/helpers';
import { DateTime } from 'luxon';

const Field = ({ field, definition, error = false }) => {
    const { i18n } = useTranslation();

    const renderHelperText = () => (
        <>
            {extractBestTranslation(definition.helperText, i18n.language)}
            {error && (
                <span style={{ display: 'block', color: 'error.main' }}>
                    {error}
                </span>
            )}
        </>
    );

    const commonProps = {
        ...field,
        required: definition.required,
        fullWidth: true,
        label: extractBestTranslation(definition.label, i18n.language),
        variant: "outlined",
        error: Boolean(error) ? true : undefined
    };

    switch (definition.type) {
        default:
        case 'text':
            return <TextField {...commonProps} helperText={renderHelperText()} />;

        case 'textarea':
            return (
                <TextField
                    {...commonProps}
                    multiline
                    minRows={2}
                    maxRows={8}
                    helperText={renderHelperText()}
                />
            );

        case 'date':
            return (
                <DatePicker
                    {...commonProps}
                    value={commonProps.value ? DateTime.fromISO(commonProps.value, { zone: 'UTC' }).set({ hour: 12 }) : null}
                    timezone="UTC"
                    slotProps={{
                        textField: {
                            error: Boolean(error),
                            helperText: renderHelperText(),
                            required: definition.required
                        }
                    }}
                />
            );

        case 'datetime':
            return (
                <DateTimePicker
                    {...commonProps}
                    value={commonProps.value ? DateTime.fromISO(commonProps.value, { zone: 'UTC' }) : null}
                    timezone="UTC"
                    ampm={false}
                    slotProps={{
                        textField: {
                            error: Boolean(error),
                            helperText: renderHelperText(),
                            required: definition.required
                        }
                    }}
                />
            );

        case 'checkbox':
            if (commonProps.fullWidth) {
                delete commonProps.fullWidth;
            }
            if (commonProps.error) {
                delete commonProps.error;
            }
            return (
                <>
                    <FormControlLabel control={<Checkbox />} label={commonProps.label} {...commonProps} />
                    <FormHelperText sx={{ mt: 0 }}>{renderHelperText()}</FormHelperText>
                </>
            );

        case 'switch':
            return (
                <>
                    <FormControlLabel control={<Switch />} label={commonProps.label} {...commonProps} />
                    <FormHelperText sx={{ mt: 0 }}>{renderHelperText()}</FormHelperText>
                </>
            );

        case 'select':
            return (
                <FormControl
                    fullWidth
                    error={Boolean(error)}
                    required={definition.required}
                >
                    <InputLabel>{commonProps.label}</InputLabel>
                    <Select {...commonProps}
                        multiple={definition.type === 'selectmultiple'}>
                        {Object.entries(definition.enum || {}).map(([value, labels]) => (
                            <MenuItem key={value} value={value}>
                                {extractBestTranslation(labels, i18n.language)}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>{renderHelperText()}</FormHelperText>
                </FormControl>
            );

        case 'selectmultiple':
            return (
                <FormControl
                    fullWidth
                    error={Boolean(error)}
                    required={definition.required}
                >
                    <InputLabel>{commonProps.label}</InputLabel>
                    <Select multiple {...commonProps}
                        renderValue={(selected) => Object.keys(definition.enum || {})
                            .filter(key => selected.includes(key))
                            .map(value => extractBestTranslation(definition.enum[value], i18n.language))
                            .join(', ')
                        }>
                        {Object.entries(definition.enum || {}).map(([value, labels]) => (
                            <MenuItem key={value} value={value}>
                                <Checkbox checked={commonProps.value.includes(value)} />
                                {extractBestTranslation(labels, i18n.language)}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>{renderHelperText()}</FormHelperText>
                </FormControl>
            );

        case 'radios':
            if (commonProps.fullWidth) {
                delete commonProps.fullWidth;
            }
            return (
                <FormControl
                    fullWidth
                    error={Boolean(error)}
                    required={definition.required}
                >
                    <FormLabel>{commonProps.label}</FormLabel>
                    <FormHelperText sx={{ ml: 0}}>{renderHelperText()}</FormHelperText>
                    <RadioGroup {...commonProps}>
                        {Object.entries(definition.enum || {}).map(([value, labels]) => (
                            <FormControlLabel
                                key={value}
                                value={value}
                                control={<Radio />}
                                label={extractBestTranslation(labels, i18n.language)}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            );

        case 'checkboxes':
            return (
                <FormControl
                    fullWidth
                    error={Boolean(error)}
                    required={definition.required}
                >
                    <FormLabel>{commonProps.label}</FormLabel>
                    <FormHelperText sx={{ ml: 0}}>{renderHelperText()}</FormHelperText>
                    <FormGroup>
                        {Object.entries(definition.enum || {}).map(([value, labels]) => (
                            <FormControlLabel
                                key={value}
                                control={
                                    <Checkbox 
                                        checked={commonProps.value?.includes(value)}
                                        onChange={(e) => {
                                            const newValue = e.target.checked
                                                ? [...(commonProps.value || []), value]
                                                : (commonProps.value || []).filter(v => v !== value);
                                            commonProps.onChange({ target: { value: newValue } });
                                        }}
                                    />
                                }
                                label={extractBestTranslation(labels, i18n.language)}
                            />
                        ))}
                    </FormGroup>
                </FormControl>
            );

        case 'info':
            return <Typography>{commonProps.label}</Typography>;
    }
};

export default Field;
